// game/client/src/hud/direction-arrows.ts
//
// On-screen directional arrows (playtest feedback #7 — cold is too punishing).
// Two guidance overlays, both screen-edge anchored like hud/offscreen-hero.ts:
//
//   • CAMPFIRE ARROW (part 2) — while the local player is FREEZING, an amber
//     arrow pins to the screen edge pointing toward the nearest friendly Heat
//     source (Campfire/Tavern — only same-team fires actually warm you, since
//     the Heat aura is same-team). Hides when warm again or when no fire exists,
//     and when the fire is already comfortably on-screen (you can see it).
//
//   • TEAMMATE ARROWS (part 3) — subtle light-green arrows pointing toward
//     same-team allies who are OFF-screen, so you can find your group (and the
//     fires they build). On-screen allies need no arrow; capped to the nearest
//     few so the edges never clutter.
//
// main.ts feeds it the local cube + room state + camera each frame; this module
// owns all the DOM + projection math. Presentational + dependency-light, matching
// the other hud/ widgets. The sister freeze HINT ("find a fire") lives in
// hud/onboarding.ts — this module is the spatial pointer, that one is the text.

import type { Cube, HelloState } from "@tribez/shared";
import * as THREE from "three";

// Freeze on/off thresholds mirror the sticky warning in hud/onboarding.ts so the
// arrow and the "find a fire" hint appear and clear together. Hysteresis (local
// latch) avoids flicker around the boundary.
const FREEZE_ON = 30;   // warmth ≤ this → start showing the campfire arrow
const FREEZE_OFF = 55;  // warmth ≥ this → stop showing it

const FIRE_TYPES = new Set(["Campfire", "Tavern"]);
const MAX_TEAMMATE_ARROWS = 4;  // cap edge clutter; nearest allies win
const EDGE_INSET = 30;          // px inset from viewport edge where arrows ride

export interface DirArrowsCtx {
  me: Cube;                                   // local player's cube (alive)
  state: HelloState;                          // room state (cubes, buildings)
  mySessionId: string;
  camera: THREE.Camera;
  sampleHeight: (x: number, z: number) => number;
}

export interface DirectionArrows {
  /** Update both overlays from live state. Pass null to hide everything (dead /
   *  not loaded). camera.updateMatrixWorld() is assumed already current. */
  update(ctx: DirArrowsCtx | null): void;
  dispose(): void;
}

const STYLE_ID = "direction-arrows-style";
const CSS = `
.dir-arrow {
  position: fixed; left: 0; top: 0; z-index: 46; display: none;
  width: 34px; height: 34px; margin: -17px 0 0 -17px;  /* center on (left,top) */
  pointer-events: none;
}
.dir-arrow.show { display: block; }
.dir-arrow__rot { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; }
.dir-arrow__rot svg { width: 22px; height: 22px; display: block; }

/* Campfire arrow — warm amber, gently pulsing so a freezing player notices it. */
.dir-arrow--fire .dir-arrow__rot { color: #ffb24d; }
.dir-arrow--fire {
  width: 40px; height: 40px; margin: -20px 0 0 -20px;
  filter: drop-shadow(0 0 6px rgba(255, 150, 50, 0.85));
  animation: dir-fire-pulse 1.3s ease-in-out infinite;
}
@keyframes dir-fire-pulse {
  0%, 100% { filter: drop-shadow(0 0 5px rgba(255,150,50,0.55)); }
  50%      { filter: drop-shadow(0 0 11px rgba(255,180,80,0.95)); }
}

/* Teammate arrows — subtle light green, no pulse, translucent. */
.dir-arrow--ally .dir-arrow__rot { color: #9be08a; }
.dir-arrow--ally { opacity: 0.62; filter: drop-shadow(0 0 3px rgba(120, 220, 110, 0.5)); }

/* Corpse arrow — prominent bone-white skull marker pointing back to your own
   dropped gear after death. Larger + pulsing so it's easy to find. */
.dir-arrow--corpse .dir-arrow__rot { color: #f3ede0; }
.dir-arrow--corpse {
  width: 42px; height: 42px; margin: -21px 0 0 -21px;
  filter: drop-shadow(0 0 6px rgba(255, 255, 255, 0.8));
  animation: dir-corpse-pulse 1.5s ease-in-out infinite;
}
.dir-arrow--corpse .dir-arrow__rot svg { width: 24px; height: 24px; }
@keyframes dir-corpse-pulse {
  0%, 100% { filter: drop-shadow(0 0 5px rgba(255,255,255,0.5)); }
  50%      { filter: drop-shadow(0 0 12px rgba(255,255,255,0.95)); }
}
/* Skull + "loot" tag rides under the corpse arrow (does NOT rotate with it), so
   the marker reads unmistakably as "your dropped gear is this way". */
.dir-arrow__tag {
  position: absolute; top: 100%; left: 50%; transform: translate(-50%, 3px);
  white-space: nowrap; font: 800 10px/1 system-ui, sans-serif; letter-spacing: .04em;
  color: #1a1206; background: #f3ede0; border: 1px solid #c9c2b2;
  padding: 2px 6px; border-radius: 999px; box-shadow: 0 1px 4px rgba(0,0,0,0.6);
}
`;

const ARROW_SVG =
  `<svg viewBox="0 0 24 24"><path d="M4 12 H17 M12 6 L18 12 L12 18" stroke="currentColor" stroke-width="2.6" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

/** Project a world point to a screen-edge placement + facing, or null if it is
 *  on-screen (in front of the camera and inside NDC). Mirrors offscreen-hero. */
function edgePlacement(
  world: THREE.Vector3,
  camera: THREE.Camera,
  v: THREE.Vector3,
): { px: number; py: number; rot: number } | null {
  v.copy(world).project(camera);
  const behind = v.z > 1; // point behind the camera → NDC is mirrored
  const nx = behind ? -v.x : v.x;
  const ny = behind ? -v.y : v.y;
  const onScreen = !behind && nx >= -1 && nx <= 1 && ny >= -1 && ny <= 1;
  if (onScreen) return null;

  const w = window.innerWidth;
  const h = window.innerHeight;
  // NDC → screen-space direction from center (NDC y is up, screen y is down).
  let sx = nx;
  let sy = -ny;
  const len = Math.hypot(sx, sy) || 1;
  sx /= len; sy /= len;

  const halfW = w / 2 - EDGE_INSET;
  const halfH = h / 2 - EDGE_INSET;
  const tx = sx !== 0 ? halfW / Math.abs(sx) : Infinity;
  const ty = sy !== 0 ? halfH / Math.abs(sy) : Infinity;
  const t = Math.min(tx, ty);
  return { px: w / 2 + sx * t, py: h / 2 + sy * t, rot: Math.atan2(sy, sx) };
}

export function createDirectionArrows(): DirectionArrows {
  if (!document.getElementById(STYLE_ID)) {
    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  function makeArrow(kind: "fire" | "ally" | "corpse"): { root: HTMLDivElement; rot: HTMLDivElement } {
    const root = document.createElement("div");
    root.className = `dir-arrow dir-arrow--${kind}`;
    // The corpse marker gets a non-rotating "☠ loot" tag so it's unmistakable.
    const tag = kind === "corpse" ? `<div class="dir-arrow__tag">☠ loot</div>` : "";
    root.innerHTML = `<div class="dir-arrow__rot">${ARROW_SVG}</div>${tag}`;
    document.body.appendChild(root);
    return { root, rot: root.querySelector<HTMLDivElement>(".dir-arrow__rot")! };
  }

  const fireArrow = makeArrow("fire");
  const corpseArrow = makeArrow("corpse");
  const allyArrows: Array<{ root: HTMLDivElement; rot: HTMLDivElement }> = [];

  let freezing = false; // hysteresis latch for the campfire arrow
  const _v = new THREE.Vector3();
  const _w = new THREE.Vector3();

  function place(el: { root: HTMLDivElement; rot: HTMLDivElement }, p: { px: number; py: number; rot: number } | null): void {
    if (!p) { el.root.classList.remove("show"); return; }
    el.root.style.left = `${p.px}px`;
    el.root.style.top = `${p.py}px`;
    el.rot.style.transform = `rotate(${p.rot}rad)`;
    el.root.classList.add("show");
  }

  function hideAll(): void {
    fireArrow.root.classList.remove("show");
    corpseArrow.root.classList.remove("show");
    for (const a of allyArrows) a.root.classList.remove("show");
  }

  function update(ctx: DirArrowsCtx | null): void {
    if (!ctx || ctx.me.state !== "alive") { freezing = false; hideAll(); return; }
    const { me, state, mySessionId, camera, sampleHeight } = ctx;

    // ── Campfire arrow ──────────────────────────────────────────────────────
    const warmth = me.survival?.warmth ?? 100;
    if (!freezing && warmth <= FREEZE_ON) freezing = true;
    else if (freezing && warmth >= FREEZE_OFF) freezing = false;

    let fire: { x: number; z: number } | null = null;
    if (freezing) {
      let bestSq = Infinity;
      state.buildings.forEach((b) => {
        if (b.state !== "ready") return;
        if (b.teamId !== me.teamId) return;       // only same-team fires warm me
        if (!FIRE_TYPES.has(b.buildingType)) return;
        const dx = b.x - me.x, dz = b.z - me.z;
        const sq = dx * dx + dz * dz;
        if (sq < bestSq) { bestSq = sq; fire = { x: b.x, z: b.z }; }
      });
    }
    if (fire) {
      const f = fire as { x: number; z: number };
      _w.set(f.x, sampleHeight(f.x, f.z) + 1.0, f.z);
      place(fireArrow, edgePlacement(_w, camera, _v));
    } else {
      fireArrow.root.classList.remove("show");
    }

    // ── Teammate arrows ─────────────────────────────────────────────────────
    // Nearest same-team alive allies (bots + humans) that are off-screen, capped.
    const allies: Array<{ x: number; z: number; sq: number }> = [];
    state.cubes.forEach((c, sid) => {
      if (sid === mySessionId) return;
      if (c.teamId !== me.teamId) return;
      if (c.state !== "alive") return;
      const dx = c.x - me.x, dz = c.z - me.z;
      allies.push({ x: c.x, z: c.z, sq: dx * dx + dz * dz });
    });
    allies.sort((a, b) => a.sq - b.sq);

    let used = 0;
    for (let i = 0; i < allies.length && used < MAX_TEAMMATE_ARROWS; i++) {
      const a = allies[i]!;
      _w.set(a.x, sampleHeight(a.x, a.z) + 1.0, a.z);
      const p = edgePlacement(_w, camera, _v);
      if (!p) continue;  // on-screen ally → no arrow
      if (used >= allyArrows.length) allyArrows.push(makeArrow("ally"));
      place(allyArrows[used]!, p);
      used++;
    }
    for (let i = used; i < allyArrows.length; i++) allyArrows[i]!.root.classList.remove("show");

    // ── Corpse arrow ──────────────────────────────────────────────────────────
    // Point back to the player's OWN corpse so they can recover dropped gear after
    // respawning. The server keeps a player's own corpse always-visible, so this
    // works across the whole map; nearest own-corpse wins if several deaths stack.
    // Hides when the corpse is on-screen (you can see the loot) or already looted/
    // expired (gone from state). While dead, the early-return above hides it (the
    // respawn overlay is showing instead).
    let corpse: { x: number; z: number } | null = null;
    let bestCorpseSq = Infinity;
    state.corpses.forEach((cp) => {
      if (cp.ownerName !== mySessionId) return;
      const dx = cp.x - me.x, dz = cp.z - me.z;
      const sq = dx * dx + dz * dz;
      if (sq < bestCorpseSq) { bestCorpseSq = sq; corpse = { x: cp.x, z: cp.z }; }
    });
    if (corpse) {
      const cp = corpse as { x: number; z: number };
      _w.set(cp.x, sampleHeight(cp.x, cp.z) + 1.0, cp.z);
      place(corpseArrow, edgePlacement(_w, camera, _v));
    } else {
      corpseArrow.root.classList.remove("show");
    }
  }

  function dispose(): void {
    fireArrow.root.remove();
    corpseArrow.root.remove();
    for (const a of allyArrows) a.root.remove();
    allyArrows.length = 0;
  }

  return { update, dispose };
}
